<template>
  <aside class="woot-sidebar" :class="{ 'nav-floating': isFloatingOpen }">
    <primary-sidebar
      :logo-source="globalConfig.logoThumbnail"
      :installation-name="globalConfig.installationName"
      :is-a-custom-branded-instance="isACustomBrandedInstance"
      :account-id="accountId"
      :menu-items="primaryMenuItems"
      :active-menu-item="activePrimaryMenu.key"
      @toggle-accounts="toggleAccountModal"
      @open-notification-panel="openNotificationPanel"
    />
    <!-- Gatilho na borda para reabrir o menu quando está recolhido (só no chat) -->
    <button
      v-if="hasSecondaryMenu && isChatView && !effectiveShowSecondary"
      class="nav-peek-trigger"
      :title="$t('SIDEBAR_ITEMS.MENU_CONTROLS.SHOW')"
      @click="openMenu"
    >
      <svg viewBox="0 0 24 24"><path d="M9 6l6 6-6 6" /></svg>
    </button>
    <div v-if="hasSecondaryMenu" class="secondary-sidebar">
      <div
        v-if="effectiveShowSecondary && isChatView"
        class="secondary-sidebar__controls"
      >
        <button
          class="nav-hbtn"
          :class="{ 'is-active': isNavPinned }"
          :title="
            isNavPinned
              ? $t('SIDEBAR_ITEMS.MENU_CONTROLS.UNPIN')
              : $t('SIDEBAR_ITEMS.MENU_CONTROLS.PIN')
          "
          @click="togglePin"
        >
          <svg viewBox="0 0 24 24">
            <path d="M9 4h6l-1 6 3 3v2H7v-2l3-3-1-6Z" />
            <path d="M12 15v5" />
          </svg>
        </button>
        <button
          class="nav-hbtn"
          :title="$t('SIDEBAR_ITEMS.MENU_CONTROLS.COLLAPSE')"
          @click="closeMenu"
        >
          <svg viewBox="0 0 24 24"><path d="M15 6l-6 6 6 6" /></svg>
        </button>
      </div>
      <secondary-sidebar
        v-if="effectiveShowSecondary"
        :class="sidebarClassName"
        :account-id="accountId"
        :inboxes="inboxes"
        :labels="labels"
        :teams="teams"
        :dashboard-embed="records"
        :custom-views="customViews"
        :menu-config="activeSecondaryMenu"
        :current-role="currentRole"
        :is-on-chatwoot-cloud="isOnChatwootCloud"
        @add-label="showAddLabelPopup"
        @toggle-accounts="toggleAccountModal"
        @contacts-view="toggleContactsView"
        @click.native="onNavItemClick"
      />
    </div>
  </aside>
</template>

<script>
import { mapGetters } from 'vuex';
import adminMixin from '../../mixins/isAdmin';
import { canAccessCRM } from '../../helper/crmPermissionHelper';
import { canAccessWise } from '../../helper/wiseAccess';
import { FEATURE_FLAGS } from '../../featureFlags';
import { getSidebarItems } from './config/default-sidebar';
import alertMixin from 'shared/mixins/alertMixin';

import PrimarySidebar from './sidebarComponents/Primary';
import SecondarySidebar from './sidebarComponents/Secondary';
import {
  hasPressedAltAndCKey,
  hasPressedAltAndRKey,
  hasPressedAltAndSKey,
  hasPressedAltAndVKey,
  hasPressedCommandAndForwardSlash,
  isEscape,
} from 'shared/helpers/KeyboardHelpers';
import eventListenerMixins from 'shared/mixins/eventListenerMixins';
import { BUS_EVENTS } from 'shared/constants/busEvents';
import router from '../../routes';

export default {
  components: {
    PrimarySidebar,
    SecondarySidebar,
  },
  mixins: [adminMixin, alertMixin, eventListenerMixins],
  props: {
    showSecondarySidebar: {
      type: Boolean,
      default: true,
    },
    sidebarClassName: {
      type: String,
      default: '',
    },
  },
  data() {
    return {
      showOptionsMenu: false,
      timerInterval: 60000,
      interval: null,
      navMode: 'pinned',
    };
  },

  computed: {
    ...mapGetters({
      accountId: 'getCurrentAccountId',
      currentRole: 'getCurrentRole',
      currentUser: 'getCurrentUser',
      globalConfig: 'globalConfig/get',
      inboxes: 'inboxes/getInboxes',
      isACustomBrandedInstance: 'globalConfig/isACustomBrandedInstance',
      isFeatureEnabledonAccount: 'accounts/isFeatureEnabledonAccount',
      isOnChatwootCloud: 'globalConfig/isOnChatwootCloud',
      labels: 'labels/getLabelsOnSidebar',
      records: 'dashboardApps/getRecords',
      teams: 'teams/getMyTeams',
      currentAccountId: 'getCurrentAccountId',
      getAccount: 'accounts/getAccount',
      getFeatureFlag: 'featureFlag/getFeatureFlag',
      kanbanList: 'getKanbanfunnels',
      integrationList: 'integrations/getIntegration',
    }),
    account() {
      return this.getAccount(this.currentAccountId);
    },
    isNavPinned() {
      return this.navMode === 'pinned';
    },
    // Há um menu secundário para a seção atual?
    hasSecondaryMenu() {
      return !!(
        this.activeSecondaryMenu &&
        Object.keys(this.activeSecondaryMenu).length
      );
    },
    // O menu ocultável/flutuante só existe no chatboard (página do chat).
    // Nas demais seções o menu secundário é sempre fixo.
    isChatView() {
      return this.activePrimaryMenu.key === 'conversations';
    },
    // Fora do chat, o menu secundário é sempre exibido (fixo), ignorando o
    // toggle global de recolher.
    effectiveShowSecondary() {
      return this.isChatView ? this.showSecondarySidebar : true;
    },
    // Menu flutuante aberto (sobrepõe o conteúdo) — apenas no chat
    isFloatingOpen() {
      return (
        this.isChatView && !this.isNavPinned && this.showSecondarySidebar
      );
    },
    activeCustomView() {
      if (this.activePrimaryMenu.key === 'contacts') {
        return 'contact';
      }
      if (this.activePrimaryMenu.key === 'conversations') {
        return 'conversation';
      }
      return '';
    },
    customViews() {
      return this.$store.getters['customViews/getCustomViewsByFilterType'](
        this.activeCustomView
      );
    },
    isConversationOrContactActive() {
      return (
        this.activePrimaryMenu.key === 'contacts' ||
        this.activePrimaryMenu.key === 'conversations'
      );
    },
    sideMenuConfig() {
      const getFeatureFlag = this.getFeatureFlag();
      return getSidebarItems(this.accountId, getFeatureFlag);
    },

    userAssignmentKanban() {
      return canAccessCRM(this.account, this.currentUser, this.kanbanList);
    },
    primaryMenuItems() {
      const menuItems = this.sideMenuConfig.primaryMenu;
      const integration = this.integrationList('wise').hooks;

      const menuItemsFilter = menuItems.filter(menuItem => {
        const isAvailableForTheUser = menuItem.roles.includes(this.currentRole);
        if (menuItem.key === 'crm' && this.userAssignmentKanban) {
          return true;
        }

        if (
          menuItem.key === 'campaigns_flow' &&
          this.account?.enable_campaign_flow
        ) {
          return true;
        }
        if (menuItem.key === 'wise') {
          if (integration && !integration.length) {
            return false;
          }
          if (!this.isFeatureEnabledonAccount(this.accountId, FEATURE_FLAGS.WISE)) {
            return false;
          }
          if (!canAccessWise(this.currentUser)) {
            return false;
          }
        }

        if (
          menuItem.key === 'typebot' &&
          !(this.currentUser?.type == 'SuperAdmin' || this.isAdministrator || this.currentUser?.permissions?.see_typebot_user)
        ) {
          return false;
        }

        if (menuItem.key === 'dashboard_embed') {
          return this.currentUser?.type === 'SuperAdmin' || this.isAdmin;
        }

        if (
          !isAvailableForTheUser ||
          (!this.userAssignmentKanban && menuItem.key == 'crm')
        ) {
          return false;
        }

        if (menuItem.featureFlag) {
          return this.isFeatureEnabledonAccount(
            this.accountId,
            menuItem.featureFlag
          );
        }
        return true;
      });
      if (this.isSupervisor) {
        return this.supervisorPermissions(menuItemsFilter);
      }
      return menuItemsFilter;
    },
    activeSecondaryMenu() {
      const { secondaryMenu } = this.sideMenuConfig;
      const { name: currentRoute } = this.$route;

      const activeSecondaryMenu =
        secondaryMenu.find(menuItem =>
          menuItem.routes.includes(currentRoute)
        ) || {};
      return activeSecondaryMenu;
    },
    activePrimaryMenu() {
      const activePrimaryMenu =
        this.primaryMenuItems.find(
          menuItem => menuItem.key === this.activeSecondaryMenu.parentNav
        ) || {};
      return activePrimaryMenu;
    },
  },

  watch: {
    activeCustomView() {
      this.fetchCustomViews();
    },
    isFloatingOpen(isOpen) {
      if (isOpen) {
        // adia p/ não capturar o próprio clique que abriu o menu
        setTimeout(() => {
          document.addEventListener('click', this.onDocumentClick);
        }, 0);
      } else {
        document.removeEventListener('click', this.onDocumentClick);
      }
    },
  },
  beforeDestroy() {
    document.removeEventListener('click', this.onDocumentClick);
  },
  async mounted() {
    const savedNav = localStorage.getItem('omnidesk_nav_mode');
    if (savedNav === 'floating' || savedNav === 'pinned') {
      this.navMode = savedNav;
    }
    this.$store.dispatch('labels/get');
    this.$store.dispatch('featureFlag/get');
    this.$store.dispatch('inboxes/get');
    this.$store.dispatch('notifications/unReadCount');
    this.$store.dispatch('teams/get');
    this.$store.dispatch('attributes/get');
    this.$store.dispatch('dashboardApps/get');
    await this.$store.dispatch('integrations/get');
    this.fetchCustomViews();
    this.updateInboxes();
  },

  methods: {
    setNavMode(mode) {
      this.navMode = mode;
      localStorage.setItem('omnidesk_nav_mode', mode);
    },
    togglePin() {
      // Alterna fixado <-> flutuante. Ao fixar, garante o menu visível.
      const next = this.isNavPinned ? 'floating' : 'pinned';
      this.setNavMode(next);
      if (next === 'pinned' && !this.showSecondarySidebar) {
        this.openMenu();
      }
    },
    openMenu() {
      // Abrir o menu = toggle nativo (mesmo mecanismo do ícone "Todas as conversas")
      if (!this.showSecondarySidebar) {
        bus.$emit(BUS_EVENTS.TOGGLE_SIDEMENU);
      }
    },
    closeMenu() {
      if (this.showSecondarySidebar) {
        bus.$emit(BUS_EVENTS.TOGGLE_SIDEMENU);
      }
    },
    onNavItemClick(e) {
      // No modo flutuante, selecionar um item recolhe o menu (mantém aberto se fixado)
      if (this.isFloatingOpen && e.target.closest('a')) {
        this.closeMenu();
      }
    },
    onDocumentClick(e) {
      // Fecha o menu flutuante ao clicar fora dele
      if (!this.isFloatingOpen) return;
      const t = e.target;
      if (
        t.closest('.secondary-sidebar') ||
        t.closest('.nav-peek-trigger') ||
        t.closest('.toggle-sidebar')
      ) {
        return;
      }
      this.closeMenu();
    },
    supervisorPermissions(list) {
      const user = this.currentUser.permissions;
      const listPermissions = list.filter(page => {
        switch (page.key) {
          case 'dashboard':
            return user.dashboard;
          case 'contacts':
            return user.contacts;
          case 'campaigns':
            return user.campaigns;
          case 'helpcenter':
            return user.help_center;
          case 'reports':
            return user.reports;
          default:
            return page;
        }
      });
      return listPermissions;
    },
    fetchCustomViews() {
      if (this.isConversationOrContactActive) {
        this.$store.dispatch('customViews/get', this.activeCustomView);
      }
    },
    toggleKeyShortcutModal() {
      this.$emit('open-key-shortcut-modal');
    },
    closeKeyShortcutModal() {
      this.$emit('close-key-shortcut-modal');
    },
    handleKeyEvents(e) {
      if (hasPressedCommandAndForwardSlash(e)) {
        this.toggleKeyShortcutModal();
      }
      if (isEscape(e)) {
        this.closeKeyShortcutModal();
        if (this.isFloatingOpen) {
          this.closeMenu();
        }
      }

      if (hasPressedAltAndCKey(e)) {
        if (!this.isCurrentRouteSameAsNavigation('home')) {
          router.push({ name: 'home' });
        }
      } else if (hasPressedAltAndVKey(e)) {
        if (!this.isCurrentRouteSameAsNavigation('contacts_dashboard')) {
          router.push({ name: 'contacts_dashboard' });
        }
      } else if (hasPressedAltAndRKey(e)) {
        if (!this.isCurrentRouteSameAsNavigation('settings_account_reports')) {
          router.push({ name: 'settings_account_reports' });
        }
      } else if (hasPressedAltAndSKey(e)) {
        if (!this.isCurrentRouteSameAsNavigation('agent_list')) {
          router.push({ name: 'agent_list' });
        }
      }
    },
    isCurrentRouteSameAsNavigation(routeName) {
      return this.$route.name === routeName;
    },
    toggleSupportChatWindow() {
      window.$chatwoot.toggle();
    },
    toggleAccountModal() {
      this.$emit('toggle-account-modal');
    },
    showAddLabelPopup() {
      this.$emit('show-add-label-popup');
    },
    openNotificationPanel() {
      this.$emit('open-notification-panel');
    },
    toggleContactsView() {
      this.$emit('toggle-contacts-view');
    },
    updateInboxes() {
      this.$store.dispatch('inboxes/get');
    },
    created() {
      this.interval = setInterval(() => {
        this.updateInboxes();
      }, this.timerInterval);
    },
    destroyed() {
      clearInterval(this.interval);
    },
  },
};
</script>

<style lang="scss" scoped>
.woot-sidebar {
  background: var(--w-950);
  display: flex;
  min-height: 0;
  height: 100%;
  width: fit-content;
  position: relative;
}

.secondary-sidebar {
  position: relative;
  display: flex;
  flex-direction: column;
  overflow: hidden;
  // fundo branco também no modo fixado (remove a faixa azul do navy vazando atrás dos controles)
  background: var(--color-surface);
  // linha divisória entre o menu e a listagem de conversa (modo fixado)
  border-right: 1px solid var(--color-border);
}

// O menu interno ocupa a altura restante e usa o próprio scroll (sem barra externa)
.secondary-sidebar ::v-deep .secondary-menu {
  flex: 1;
  min-height: 0;
  height: auto;
}

// Flutuante: o menu sobrepõe o conteúdo, com elevação (não empurra)
.woot-sidebar.nav-floating .secondary-sidebar {
  position: absolute;
  left: 7.6rem; // largura do rail principal (Primary.vue) — encosta na borda, sem sobrepor
  top: 5.6rem; // abaixo da barra superior (56px) — controles não ficam atrás dela
  bottom: 0;
  width: 20rem;
  z-index: 45;
  background: var(--color-surface);
  box-shadow: 14px 0 44px rgba(0, 33, 54, 0.16);
  animation: nav-slide-in 0.2s ease;
}

@keyframes nav-slide-in {
  from {
    transform: translateX(-10px);
    opacity: 0.3;
  }
  to {
    transform: translateX(0);
    opacity: 1;
  }
}

// Controles (fixar / recolher) — barra no topo do menu
.secondary-sidebar__controls {
  flex: 0 0 auto;
  display: flex;
  justify-content: flex-end;
  align-items: center;
  gap: var(--space-micro);
  padding: var(--space-small) var(--space-small) 0;
}

.nav-hbtn {
  width: 26px;
  height: 26px;
  border-radius: 7px;
  display: grid;
  place-items: center;
  color: var(--s-400);
  cursor: pointer;
  transition: background 0.15s ease-in-out, color 0.15s ease-in-out;

  &:hover {
    background: var(--s-50);
    color: var(--s-700);
  }

  &.is-active {
    background: var(--color-woot-tint);
    color: var(--color-woot);
  }

  svg {
    width: 15px;
    height: 15px;
    fill: none;
    stroke: currentColor;
    stroke-width: 2;
    stroke-linecap: round;
    stroke-linejoin: round;
  }
}

// Gatilho de "espiar" na borda (quando recolhido)
.nav-peek-trigger {
  position: absolute;
  left: calc(7.6rem - 1px); // encostado na borda direita do rail principal
  top: 50%;
  transform: translateY(-50%);
  z-index: 44;
  width: 18px;
  height: 52px;
  display: flex;
  align-items: center;
  justify-content: center;
  background: var(--color-surface);
  border: 1px solid var(--color-border);
  border-left: 0;
  border-radius: 0 8px 8px 0;
  color: var(--s-500);
  box-shadow: 2px 0 8px rgba(0, 33, 54, 0.08);
  cursor: pointer;
  transition: color 0.15s ease-in-out, background 0.15s ease-in-out;

  &:hover {
    color: var(--color-woot);
    background: var(--s-25);
  }

  svg {
    width: 14px;
    height: 14px;
    fill: none;
    stroke: currentColor;
    stroke-width: 2;
    stroke-linecap: round;
    stroke-linejoin: round;
  }
}
</style>

<style lang="scss">
@import '~dashboard/assets/scss/variables';

.account-selector--modal,
.kanban-selector--modal {
  .modal-container {
    width: 40rem;
  }
}

.secondary-sidebar {
  overflow-y: auto;
  height: 100%;
}

.account-selector {
  cursor: pointer;
  padding: $space-small $space-large;

  .selected--account,
  .selected--kanban {
    margin-top: -$space-smaller;

    & + .account--details {
      padding-left: $space-normal - $space-micro;
    }
  }

  .account--details {
    padding-left: $space-large + $space-smaller;
  }

  &:last-child {
    margin-bottom: $space-large;
  }

  a {
    align-items: center;
    cursor: pointer;
    display: flex;

    .account--name {
      cursor: pointer;
      font-size: $font-size-medium;
      font-weight: $font-weight-medium;
      line-height: 1;
    }

    .account--role {
      cursor: pointer;
      font-size: $font-size-mini;
      text-transform: capitalize;
    }
  }
}

.app-context-menu {
  align-items: center;
  cursor: pointer;
  display: flex;
  flex-direction: row;
  height: 6rem;
}

.current-user--options {
  font-size: $font-size-big;
  margin-bottom: auto;
  margin-left: auto;
  margin-top: auto;
}

.secondary-menu .nested.vertical.menu {
  margin-left: var(--space-small);
}
</style>
